import { useEffect, useRef, useState } from 'react';
import type QRCodeStyling from 'qr-code-styling';
import type { Options } from 'qr-code-styling';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import { Tooltip } from '@/shared/ui/tooltip';

/** Resolução do PNG baixado e exibido. */
const QR_SIZE = 1024;
const LOGO_SIZE = 256;

/** Mídia da api-v2 fica numa S3 sem CORS: passa pelo BFF para o canvas poder exportar. */
function sameOriginImageSrc(url: string): string {
  return /^https:\/\/[^/]+\.amazonaws\.com\//i.test(url)
    ? `/bff/assets/nps-image?url=${encodeURIComponent(url)}`
    : url;
}

/** Foto recortada em quadrado com cantos arredondados (object-cover), como data URL. */
async function roundedLogo(url: string): Promise<string | null> {
  try {
    const response = await fetch(sameOriginImageSrc(url), { credentials: 'same-origin' });
    if (!response.ok) return null;
    const bitmap = await createImageBitmap(await response.blob());
    const canvas = document.createElement('canvas');
    canvas.width = LOGO_SIZE;
    canvas.height = LOGO_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.beginPath();
    ctx.roundRect(0, 0, LOGO_SIZE, LOGO_SIZE, LOGO_SIZE * 0.22);
    ctx.clip();
    const scale = Math.max(LOGO_SIZE / bitmap.width, LOGO_SIZE / bitmap.height);
    const width = bitmap.width * scale;
    const height = bitmap.height * scale;
    ctx.drawImage(bitmap, (LOGO_SIZE - width) / 2, (LOGO_SIZE - height) / 2, width, height);
    bitmap.close();
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

function qrOptions(data: string, logo: string | null): Partial<Options> {
  return {
    type: 'canvas',
    width: QR_SIZE,
    height: QR_SIZE,
    margin: 40,
    data,
    image: logo ?? undefined,
    // H aguenta ~30% do código coberto pela imagem.
    qrOptions: { errorCorrectionLevel: logo ? 'H' : 'Q' },
    dotsOptions: { type: 'square', color: '#000000', roundSize: true },
    cornersSquareOptions: { type: 'extra-rounded', color: '#000000' },
    cornersDotOptions: { type: 'rounded', color: '#000000' },
    backgroundOptions: { color: '#ffffff' },
    // saveAsBlob baixa a imagem de novo via XHR. Com data URL a CSP (connect-src) bloqueia e o QR não desenha.
    imageOptions: { saveAsBlob: false, imageSize: 0.32, margin: 16, hideBackgroundDots: true },
  };
}

type Status = 'loading' | 'ready' | 'error';

interface SurveyQrCodeProps {
  /** Link público da pesquisa. */
  url: string;
  /** Foto de perfil do dono da pesquisa — vai no centro do QR. */
  logoUrl?: string | null;
  /** Nome do PNG baixado (sem extensão). */
  fileName: string;
}

/** QR Code do link público com a foto de perfil no centro, pronto para baixar em PNG. */
export function SurveyQrCode({ url, logoUrl, fileName }: SurveyQrCodeProps) {
  const qrRef = useRef<QRCodeStyling | null>(null);
  const [status, setStatus] = useState<Status>('loading');
  const [src, setSrc] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const objectUrls: string[] = [];
    setStatus('loading');
    setSrc(null);
    qrRef.current = null;

    (async () => {
      const { default: QRCode } = await import('qr-code-styling');
      const logo = logoUrl ? await roundedLogo(logoUrl) : null;
      if (cancelled) return;
      const qr = new QRCode(qrOptions(url, logo));
      const raw = await qr.getRawData('png');
      if (cancelled || !raw || !('arrayBuffer' in raw)) {
        if (!cancelled) setStatus('error');
        return;
      }
      const objectUrl = URL.createObjectURL(raw);
      objectUrls.push(objectUrl);
      if (cancelled) return;
      qrRef.current = qr;
      setSrc(objectUrl);
      setStatus('ready');
    })().catch(() => {
      if (!cancelled) setStatus('error');
    });

    return () => {
      cancelled = true;
      for (const objectUrl of objectUrls) URL.revokeObjectURL(objectUrl);
    };
  }, [url, logoUrl]);

  const download = async () => {
    if (!qrRef.current) return;
    setDownloading(true);
    try {
      await qrRef.current.download({ name: fileName, extension: 'png' });
    } catch {
      toast.error('Não foi possível baixar o QR Code. Tente novamente.');
    } finally {
      setDownloading(false);
    }
  };

  if (status === 'error') {
    return <p className="text-xs text-muted-foreground">Não foi possível gerar o QR Code.</p>;
  }

  return (
    <div className="flex items-end gap-3">
      <div className="relative size-44 shrink-0 overflow-hidden rounded-2xl bg-white p-1 shadow-soft ring-1 ring-black/5">
        {src ? (
          <img src={src} alt="QR Code da pesquisa" className="size-full object-contain" />
        ) : (
          <Skeleton className="absolute inset-0 rounded-2xl" />
        )}
      </div>
      <Tooltip content="Baixar QR Code (PNG)">
        <Button
          type="button"
          variant="secondary"
          size="icon"
          onClick={() => void download()}
          loading={downloading}
          disabled={status !== 'ready'}
          aria-label="Baixar QR Code"
        >
          <Download className="size-4" />
        </Button>
      </Tooltip>
    </div>
  );
}
